import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react"
import { createPortal } from "react-dom"
import { ClaimType, Sku } from "../../api/models"
import { useStoreSkuClaims, useClaim } from "../../redux/claimsSlice";
import { useLicense } from "../../redux/licensesSlice";
import { useProduct } from "../../redux/productsSlice";
import { useSku } from "../../redux/skusSlice";

export function formatPrice(price: number) {
  return price % 1 === 0 ? `$${price}` : `$${price.toFixed(2)}`
}

// Renders a product's name as a popover list item, fetching it by id.
function UpgradeProductName({ productId, accessToken }: { productId: string, accessToken: string }) {
  const { data: product } = useProduct(productId, accessToken)
  if (product === undefined)
    return <li className="upgrade-popover-item"><span className="spinner" /></li>
  return <li className="upgrade-popover-item">{product.name}</li>
}

// Resolves a traded-in license to the product it covers and reports the
// productId up so the "You'll unlock" list can exclude it. Renders nothing —
// the visible trade-in list names SKUs (the consumed entitlements), not the
// individual products.
function UpgradeOwnedProductResolver({ licenseId, accessToken, onResolved }: { licenseId: string, accessToken: string, onResolved: (productId: string) => void }) {
  const { data: license } = useLicense(licenseId, accessToken)
  const productId = license?.productId
  useEffect(() => {
    if (productId !== undefined)
      onResolved(productId)
  }, [productId, onResolved])
  return null
}

// Renders a traded-in sku's name as a popover list item, fetching it by id
// (the public store fetch — a discontinued sku still resolves).
function UpgradeSkuName({ skuId }: { skuId: string }) {
  const { data: sku } = useSku(skuId)
  if (sku === undefined)
    return <li className="upgrade-popover-item"><span className="spinner" /></li>
  return <li className="upgrade-popover-item">{sku.name}</li>
}

// One claimed product the upgrade grants. Hidden unless it's a license claim the
// user doesn't already own (i.e. not among the revoked licenses' products).
function UpgradeGainItem({ claimId, ownedProductIds, accessToken }: { claimId: string, ownedProductIds: Set<string>, accessToken: string }) {
  const { data: claim } = useClaim(claimId)
  if (claim === undefined)
    return <li className="upgrade-popover-item"><span className="spinner" /></li>
  if (claim.type !== ClaimType.License || ownedProductIds.has(claim.productId))
    return null
  return <UpgradeProductName productId={claim.productId} accessToken={accessToken} />
}

// Loads the latest version's claims (via the public sku fetch — no admin
// access needed), then renders a gain item per claim.
function UpgradeGains({ skuId, ownedProductIds, accessToken }: { skuId: string, ownedProductIds: Set<string>, accessToken: string }) {
  const { data: claimIds } = useStoreSkuClaims(skuId)
  if (claimIds === undefined)
    return <li className="upgrade-popover-item"><span className="spinner" /></li>
  return (
    <>
      {claimIds.map((id) => (
        <UpgradeGainItem key={id} claimId={id} ownedProductIds={ownedProductIds} accessToken={accessToken} />
      ))}
    </>
  )
}

// The shared hover/pin/portal scaffold behind UpgradeInfo and CrossgradeInfo:
// an (i) button that opens a fixed-position popover rendered into document.body.
// Hover opens it; a click pins it so it survives mouseleave (and serves
// keyboard/touch). The close delay lets the pointer cross the gap between the
// button and the popover without dismissing it.
function InfoPopover({ ariaLabel, children }: { ariaLabel: string, children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const pinnedRef = useRef(false)
  const closeTimerRef = useRef<number | null>(null)
  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])
  const handleHoverOpen = useCallback(() => {
    cancelClose()
    setOpen(true)
  }, [cancelClose])
  const handleHoverClose = useCallback(() => {
    if (pinnedRef.current) return
    cancelClose()
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 150)
  }, [cancelClose])
  useEffect(() => cancelClose, [cancelClose])

  const btnRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  // Rendered in a portal with fixed positioning so it can't be clipped by the
  // card/sidebar and stays within the viewport even next to the sidebar.
  const [coords, setCoords] = useState<{ top: number, left: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) { setCoords(null); return }
    const update = () => {
      const btn = btnRef.current
      const pop = popoverRef.current
      if (!btn || !pop) return
      const b = btn.getBoundingClientRect()
      const p = pop.getBoundingClientRect()
      const margin = 8
      let left = b.left + b.width / 2 - p.width / 2
      left = Math.max(margin, Math.min(left, window.innerWidth - p.width - margin))
      let top = b.top - p.height - margin
      if (top < margin) top = b.bottom + margin // flip below if there's no room above
      setCoords({ top, left })
    }
    update()
    window.addEventListener("resize", update)
    window.addEventListener("scroll", update, true)
    // Async content (product names) changes the popover height after mount;
    // reposition when that happens so "place above" stays anchored correctly.
    const observer = new ResizeObserver(update)
    if (popoverRef.current) observer.observe(popoverRef.current)
    return () => {
      window.removeEventListener("resize", update)
      window.removeEventListener("scroll", update, true)
      observer.disconnect()
    }
  }, [open])

  // Dismiss on click anywhere outside the popover/button, or on Escape.
  useEffect(() => {
    if (!open) return
    const handlePointer = (e: MouseEvent) => {
      const target = e.target as Node
      if (btnRef.current?.contains(target) || popoverRef.current?.contains(target)) return
      pinnedRef.current = false
      setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        pinnedRef.current = false
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handlePointer)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handlePointer)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open])

  return (
    <span className="upgrade-info">
      <button
        ref={btnRef}
        type="button"
        className="upgrade-info-btn"
        aria-label={ariaLabel}
        aria-expanded={open}
        onMouseEnter={handleHoverOpen}
        onMouseLeave={handleHoverClose}
        onClick={() => {
          cancelClose()
          if (open && pinnedRef.current) {
            pinnedRef.current = false
            setOpen(false)
          } else {
            pinnedRef.current = true
            setOpen(true)
          }
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 12.5A5.5 5.5 0 1 1 8 2.5a5.5 5.5 0 0 1 0 11zM7.25 6.75h1.5v4.5h-1.5v-4.5zM8 4.25a.9.9 0 1 1 0 1.8.9.9 0 0 1 0-1.8z" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={popoverRef}
          className="upgrade-popover"
          onMouseEnter={handleHoverOpen}
          onMouseLeave={handleHoverClose}
          style={{
            position: "fixed",
            top: coords?.top ?? 0,
            left: coords?.left ?? 0,
            bottom: "auto",
            transform: "none",
            visibility: coords ? "visible" : "hidden",
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </span>
  )
}

export function UpgradeInfo({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const upgrade = sku.upgrade
  const licenseIds = upgrade?.revokedLicenseIds ?? []
  // optional-chained: a deployed backend older than this build omits the field
  const tradedSkuIds = upgrade?.consumedSkuIds ?? []
  const savingsPercent = upgrade && upgrade.valueCents > 0
    ? Math.round((1 - upgrade.priceCents / upgrade.valueCents) * 100)
    : 0
  // Collect the productId of each revoked license as it resolves, so the
  // "you will get" list can filter out products the user already owns.
  const [ownedByLicense, setOwnedByLicense] = useState<Record<string, string>>({})
  const handleResolved = useCallback((licenseId: string, productId: string) => {
    setOwnedByLicense((prev) => prev[licenseId] === productId ? prev : { ...prev, [licenseId]: productId })
  }, [])
  const ownedProductIds = useMemo(() => new Set(Object.values(ownedByLicense)), [ownedByLicense])

  return (
    <InfoPopover ariaLabel="What this upgrade includes">
      {upgrade && savingsPercent > 0 && (
        <div className="upgrade-savings-badge">
          <span className="upgrade-savings-pct">{savingsPercent}%</span>
          <span className="upgrade-savings-label">off</span>
        </div>
      )}

      {/* the "was" price is the unowned products' summed Value — nothing to
          compare against when the products carry none (count-priced bundles
          need no values) */}
      {upgrade && upgrade.valueCents > upgrade.priceCents && (
        <div className="upgrade-price-compare">
          <span className="upgrade-value-was">{formatPrice(upgrade.valueCents / 100)}</span>
          <span className="upgrade-price-arrow" aria-hidden="true">→</span>
          <span className="upgrade-price-now">{formatPrice(upgrade.priceCents / 100)}</span>
        </div>
      )}

      {upgrade && upgrade.valueCents > upgrade.priceCents && (
        <div className="upgrade-savings-amount">
          You save {formatPrice((upgrade.valueCents - upgrade.priceCents) / 100)}
        </div>
      )}

      <div className="upgrade-popover-section">
        <div className="upgrade-popover-title">You'll unlock</div>
        <ul className="upgrade-popover-list upgrade-gains-list">
          <UpgradeGains skuId={sku.id} ownedProductIds={ownedProductIds} accessToken={accessToken} />
        </ul>
      </div>

      {/* invisible: resolve each traded-in license to its product so the
          "You'll unlock" list can exclude what's already owned */}
      {licenseIds.map((id) => (
        <UpgradeOwnedProductResolver
          key={id}
          licenseId={id}
          accessToken={accessToken}
          onResolved={(productId) => handleResolved(id, productId)}
        />
      ))}

      {tradedSkuIds.length > 0 && (
        <div className="upgrade-popover-section upgrade-owned-section">
          {/* deliberately NOT "trading in" — these skus' products are all
              part of the upgrade, so nothing is lost; the entitlements
              are consumed as the discount */}
          <div className="upgrade-popover-title">You already have</div>
          <ul className="upgrade-popover-list upgrade-owned-list">
            {tradedSkuIds.map((id) => (
              <UpgradeSkuName key={id} skuId={id} />
            ))}
          </ul>
          {/* no formula claim here — the price math (value gap × the
              bundle's discount rate, dollar rounding) is the backend's;
              the copy only promises the direction */}
          <div style={{ color: "var(--nfa-light-grey)", fontSize: "9pt", marginTop: 5 }}>
            Included in the upgrade — your price is lower because you already own them.
          </div>
        </div>
      )}
    </InfoPopover>
  )
}

// The crossgrade counterpart: list price → your price, and the owned products
// that earned the discount (they stay yours — nothing is traded in).
export function CrossgradeInfo({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const crossgrade = sku.crossgrade
  if (crossgrade === undefined)
    return null
  const savingsPercent = crossgrade.listPriceCents > 0
    ? Math.round((1 - crossgrade.priceCents / crossgrade.listPriceCents) * 100)
    : 0
  return (
    <InfoPopover ariaLabel="Why this price">
      {savingsPercent > 0 && (
        <div className="upgrade-savings-badge">
          <span className="upgrade-savings-pct">{savingsPercent}%</span>
          <span className="upgrade-savings-label">off</span>
        </div>
      )}

      <div className="upgrade-price-compare">
        <span className="upgrade-value-was">{formatPrice(crossgrade.listPriceCents / 100)}</span>
        <span className="upgrade-price-arrow" aria-hidden="true">→</span>
        <span className="upgrade-price-now">{crossgrade.priceCents === 0 ? "Free" : formatPrice(crossgrade.priceCents / 100)}</span>
      </div>

      {crossgrade.listPriceCents > crossgrade.priceCents && (
        <div className="upgrade-savings-amount">
          You save {formatPrice((crossgrade.listPriceCents - crossgrade.priceCents) / 100)}
        </div>
      )}

      <div className="upgrade-popover-section upgrade-owned-section">
        <div className="upgrade-popover-title">You already have</div>
        <ul className="upgrade-popover-list upgrade-owned-list">
          {crossgrade.discountProductIds.map((id) => (
            <UpgradeProductName key={id} productId={id} accessToken={accessToken} />
          ))}
        </ul>
        <div style={{ color: "var(--nfa-light-grey)", fontSize: "9pt", marginTop: 5 }}>
          Crossgrade — owning {crossgrade.discountProductIds.length === 1 ? "this product earns" : "these products earns"} you the discount. You keep {crossgrade.discountProductIds.length === 1 ? "it" : "them"}.
        </div>
      </div>
    </InfoPopover>
  )
}
